import { useState, useEffect } from "react";
import { Loader2, MessageSquare, Save, PaperclipIcon, FileImage, File as FileIcon, FileText, File, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Card,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { JournalEntry, JournalAttachment } from "@/types/journal";
import { Progress } from "@/components/ui/progress";
import { formatDate } from "@/utils/dateFormat";
import { useNavigate } from "react-router-dom";

interface JournalEntryCardProps {
  id: string;
  entry: JournalEntry;
  clientId: string;
  therapistNote: string;
  formatDate: (dateString: string) => string;
  onNoteChange: (entryId: string, content: string) => void;
}

const truncateContent = (content: string, maxLines: number = 5) => {
  const lines = content.split('\n');
  if (lines.length <= maxLines) return content;
  return lines.slice(0, maxLines).join('\n') + '\n...';
};

const JournalEntryCard = ({
  id,
  entry,
  clientId,
  therapistNote,
  formatDate,
  onNoteChange,
}: JournalEntryCardProps) => {
  const [savingNote, setSavingNote] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<JournalAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();

  const stopCardNavigation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Load attachments when the component mounts
  useEffect(() => {
    const fetchAttachments = async () => {
      if (!entry.id || !clientId) return;
      
      setLoadingAttachments(true);
      try {
        const { data, error } = await supabase.rpc('get_journal_attachments', {
          entry_id: entry.id
        });
        
        if (error) {
          console.error("Error fetching attachments:", error);
          if (error.message.includes('Permission denied')) {
            toast.error("You don't have permission to view these attachments");
          } else {
            toast.error(`Failed to load attachments: ${error.message}`);
          }
          return;
        }
        
        // Ensure the data conforms to the JournalAttachment interface
        if (data) {
          setAttachments(data as JournalAttachment[]);
        } else {
          setAttachments([]);
        }
      } catch (error: any) {
        console.error("Error fetching attachments:", error);
        toast.error(`Failed to load attachments: ${error.message || 'Unknown error'}`);
      } finally {
        setLoadingAttachments(false);
      }
    };
    
    fetchAttachments();
  }, [entry.id, clientId]);

  const handleSaveNote = async () => {
    if (!clientId) return;
    
    setSavingNote(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("User not authenticated");
      
      // Make sure we're passing all parameters as UUIDs
      const { error } = await supabase.rpc(
        'save_therapist_note',
        {
          therapist_id_param: user.id,
          client_id_param: clientId,
          entry_id_param: entry.id,
          content_param: therapistNote || ''
        }
      );
      
      if (error) throw error;
      
      toast.success("Note saved successfully");
    } catch (error: any) {
      console.error("Error saving note:", error);
      toast.error(error.message || "Failed to save note. Please try again.");
    } finally {
      setSavingNote(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <FileImage className="h-5 w-5" />;
    } else if (mimeType === 'application/pdf') {
      return <FileIcon className="h-5 w-5" />;
    } else if (mimeType === 'text/plain') {
      return <FileText className="h-5 w-5" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  const handleViewAttachment = async (attachment: JournalAttachment) => {
    try {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: true }));
      
      const { data } = await supabase.storage
        .from('journal_attachments')
        .createSignedUrl(attachment.file_path, 60 * 5); // 5 minutes
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      } else {
        toast.error('Failed to generate file URL');
      }
    } catch (error) {
      console.error('Error generating file URL:', error);
      toast.error('Failed to open file');
    } finally {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: false }));
    }
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-lg transition-all duration-200"
      onClick={() => navigate(`/therapist/client/${clientId}/journal-entry/${entry.id}`)}
    >
      <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg text-[#333B44]">{entry.mood || 'Unspecified Mood'}</CardTitle>
            <CardDescription>{formatDate(entry.created_at)}</CardDescription>
          </div>
          {entry.emotion_type && (
            <div className="text-right">
              <div className="font-medium text-[#3BAFDA]">{entry.emotion_type}</div>
              <div className="text-sm text-muted-foreground">
                Intensity: {entry.emotion_intensity}/10
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4" onClick={stopCardNavigation}>
        <p className="whitespace-pre-wrap text-left">
          {truncateContent(entry.content)}
        </p>
        {entry.media_url && (
          <div className="rounded-lg overflow-hidden border border-[#CCCCCC] flex justify-center">
            {entry.media_type === 'audio' ? (
              <audio controls src={entry.media_url} className="w-full max-w-[350px]" />
            ) : (
              <video controls src={entry.media_url} className="w-full max-w-[350px] max-h-[300px] h-auto rounded-lg object-contain" />
            )}
          </div>
        )}
        
        {/* Display attachments */}
        {loadingAttachments ? (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-5 w-5 animate-spin text-[#3BAFDA]" />
            <span className="ml-2 text-sm text-[#333B44]">Loading attachments...</span>
          </div>
        ) : attachments.length > 0 && (
          <div className="border border-[#CCCCCC] rounded-lg p-3 bg-white">
            <div className="flex items-center mb-2">
              <PaperclipIcon className="h-4 w-4 mr-2 text-[#3BAFDA]" />
              <h4 className="text-sm font-medium text-[#333B44]">Attached Files</h4>
            </div>
            <div className="space-y-2">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between bg-white p-2 rounded-md border border-[#CCCCCC]">
                  <div className="flex items-center">
                    <span className="text-[#3BAFDA] mr-2">
                      {getFileIcon(attachment.mime_type)}
                    </span>
                    <div>
                      <p className="text-sm font-medium truncate max-w-[180px] md:max-w-[300px]">
                        {attachment.filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(attachment.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewAttachment(attachment);
                    }}
                    disabled={loadingStates[attachment.id]}
                    className="h-8 w-8 p-0"
                  >
                    {loadingStates[attachment.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                    <span className="sr-only">View</span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex flex-col space-y-4 border-t border-[#CCCCCC] pt-4" onClick={stopCardNavigation}>
        <div className="w-full">
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-0 sm:justify-between sm:items-center mb-2">
            <Label htmlFor={`note-${entry.id}`} className="font-medium flex items-center text-[#333B44]">
              <MessageSquare className="mr-2 h-4 w-4" />
              Therapist Notes
            </Label>
            <Button 
              size="sm" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSaveNote();
              }}
              disabled={savingNote}
              className="bg-[#3BAFDA] hover:bg-[#2a9fc4] text-white transition-all duration-300 sm:ml-2"
            >
              {savingNote ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Note
                </>
              )}
            </Button>
          </div>
          <Textarea
            id={`note-${entry.id}`}
            placeholder="Add your professional notes about this entry here..."
            className="min-h-[100px] border-[#CCCCCC] focus:border-[#3BAFDA] transition-all duration-300"
            value={therapistNote}
            onClick={stopCardNavigation}
            onChange={(e) => onNoteChange(entry.id, e.target.value)}
          />
        </div>
      </CardFooter>
    </Card>
  );
};

export default JournalEntryCard;
